import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Empty,
  InputNumber,
  Popconfirm,
  Spin,
  Tabs,
  Tooltip as AntTooltip,
  message,
} from 'antd';
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { TbArrowCurveRight, TbRulerMeasure } from 'react-icons/tb';
import L from 'leaflet';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';
import {
  MapContainer,
  Marker,
  Polyline,
  Tooltip,
} from 'react-leaflet';
import {
  CurrentLocationButton, MapRefCapture, TraffixTileLayer, ZoomButtons,
} from '../../../common/map';
import {
  createManeuver,
  deleteManeuver,
  loadConflicts,
  loadIntersectionManeuvers,
  loadIntersectionTrafficLights,
  loadOptimizedPlan,
  optimizePhases,
  saveConflicts,
  saveIntersectionTrafficLights,
  updateManeuver,
} from '../APIs/intersectionEditorApi';
import { maneuverRouteLabel, movementTypeLabel } from '../model/catalog';
import ManeuverModal from './ManeuverModal';
import MeasurementLayer, { useDistanceMeasurement } from './MeasurementLayer';
import { TlGreenWaveIcon, intersectionIcon, trafficLightEditorIcon } from './mapIcons';

const MANEUVER_SHAPE = { color: '#A1E430', weight: 6, opacity: 0.95 };
const EMPTY_SELECTION = { maneuvers: [], trafficLights: [] };
/** All-red is a clearance interval: the SEMAFIX plan does not carry one. */
const PHASE_DEFAULTS = { cycle: 90, yellow: 5, allRed: 2 };

/**
 * Cycle and yellow of the optimizer come from the signal plan shown in the
 * "Semaforizzata" popup, so the timings are not retyped here.
 */
function phaseSeed(plan) {
  if (!plan) return PHASE_DEFAULTS;
  const green = Number(plan.green) || 0;
  const yellow = Number(plan.yellow) || 0;
  const red = Number(plan.red) || 0;
  const cycle = Number(plan.cycle) || green + yellow + red;
  return {
    cycle: cycle > 0 ? Math.round(cycle) : PHASE_DEFAULTS.cycle,
    yellow: yellow > 0 ? Math.round(yellow) : PHASE_DEFAULTS.yellow,
    allRed: PHASE_DEFAULTS.allRed,
  };
}

function normalizeManeuver(raw) {
  return {
    ...raw,
    id: raw.id,
    title: raw.title || '',
    inputType: raw.inputType || '',
    color: raw.color || '#353A0A',
    capSatResult: raw.capSatResult,
    lanes: Math.max(1, Number(raw.lanes || 1)),
    laneWidth: Math.max(0, Number(raw.laneWidth || 3.25)),
    inputSource: raw.inputSource || (raw.manual || raw.isManual ? 'manual' : 'satellite'),
    capacitySource: raw.capacitySource || (raw.manual || raw.isManual ? 'manual' : 'capsat'),
    verdeMin: raw.verdeMin,
    flussi: Array.isArray(raw.flussi) ? raw.flussi : [],
    latlngs: (Array.isArray(raw.latlngs) ? raw.latlngs : []).map((point) => ({
      lat: Number(point.lat ?? point[0]),
      lng: Number(point.lng ?? point[1]),
    })),
    manual: Boolean(raw.manual),
    visible: true,
  };
}

function maneuverPayload(maneuver, latlngs = maneuver.latlngs) {
  return {
    title: maneuver.title,
    inputType: maneuver.inputType,
    color: maneuver.color,
    capSatResult: maneuver.capSatResult ?? null,
    lanes: Math.max(1, Number(maneuver.lanes || 1)),
    laneWidth: Math.max(0, Number(maneuver.laneWidth || 3.25)),
    inputSource: maneuver.inputSource || 'manual',
    capacitySource: maneuver.capacitySource || 'manual',
    verdeMin: maneuver.verdeMin ?? 0,
    flussi: maneuver.flussi || [],
    latlngs,
    isManual: Boolean(maneuver.manual ?? maneuver.isManual),
  };
}

function endpointIcon(points, color) {
  if (points.length < 2) return null;
  const from = points[points.length - 2];
  const to = points[points.length - 1];
  const angle = Math.atan2(to.lat - from.lat, to.lng - from.lng) * (180 / Math.PI);
  return L.divIcon({
    className: 'semafix-editor-arrow-host',
    html: `<span style="color:${color};transform:rotate(${-angle}deg)">▶</span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });
}

function maneuverLengthMeters(points) {
  return points.slice(1).reduce((total, point, index) => {
    const previous = points[index];
    const latitude = ((point.lat + previous.lat) / 2) * (Math.PI / 180);
    const north = (point.lat - previous.lat) * 111320;
    const east = (point.lng - previous.lng) * 111320 * Math.cos(latitude);
    return total + Math.hypot(north, east);
  }, 0);
}

function sameGeometry(left = [], right = []) {
  if (left.length !== right.length) return false;
  return left.every((point, index) => (
    Math.abs(point.lat - right[index].lat) < 1e-9
    && Math.abs(point.lng - right[index].lng) < 1e-9
  ));
}

function newLocalId(prefix) {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? `${prefix}-${crypto.randomUUID()}`
    : `${prefix}-${Date.now()}-${Math.round(Math.random() * 1e6)}`;
}

function toggleId(list, id) {
  return list.includes(id) ? list.filter((item) => item !== id) : [...list, id];
}

function ConflictMatrix({ maneuvers, conflicts, onChange }) {
  const labels = maneuvers.map((maneuver) => (
    `${maneuver.title} - ${maneuver.inputType === 'pedonale' ? 'Pedonale' : 'Veicolare'}`
  ));
  const pairKey = (first, second) => [first, second].sort().join('::');

  if (labels.length < 2) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Servono almeno due manovre" />;

  const toggle = (first, second) => {
    const key = pairKey(first, second);
    const exists = conflicts.some(([left, right]) => pairKey(left, right) === key);
    onChange(exists
      ? conflicts.filter(([left, right]) => pairKey(left, right) !== key)
      : [...conflicts, [first, second]]);
  };

  return (
    <div className="semafix-editor-matrix-wrap">
      <table className="semafix-editor-matrix">
        <thead>
          <tr>
            <th />
            {labels.map((label) => <th key={label}>{label}</th>)}
          </tr>
        </thead>
        <tbody>
          {labels.map((row, rowIndex) => (
            <tr key={row}>
              <th>{row}</th>
              {labels.map((column, columnIndex) => (
                <td key={column} className={rowIndex === columnIndex ? 'same' : ''}>
                  {rowIndex !== columnIndex && (
                    <Checkbox
                      checked={conflicts.some(([left, right]) => pairKey(left, right) === pairKey(row, column))}
                      onChange={() => toggle(row, column)}
                    />
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OptimizedTimeline({ optimization, maneuvers, cycle, hoveredId, onHover }) {
  const phases = optimization?.phases || optimization?.optimized_fasi || [];
  if (!phases.length) return null;
  return (
    <div className="semafix-phase-results">
      <div className="semafix-phase-results-head">
        <b>Piano ottimizzato corrente</b>
        <span>{optimization.optimizedAt === 'fixture' ? 'Dati dimostrativi' : 'Salvato automaticamente'}</span>
      </div>
      {phases.map((phase, index) => {
        const maneuver = maneuvers.find((item) => (
          String(item.id) === String(phase.maneuverId) || item.title === phase.nome
        )) || maneuvers[index];
        const green = Math.round(Number(phase.green ?? phase.verde ?? phase.verde_efficace ?? phase.verde_min_ped) || 0);
        const phaseYellow = Math.round(Number(phase.yellow ?? optimization.yellow) || 0);
        const red = Math.max(0, Math.round(Number(phase.red) || cycle - green - phaseYellow));
        return (
          <div
            key={phase.maneuverId || phase.nome || index}
            className={`semafix-phase-result${hoveredId === maneuver?.id ? ' is-highlighted' : ''}`}
            onMouseEnter={() => onHover(maneuver?.id || null)}
            onMouseLeave={() => onHover(null)}
          >
            <span title={phase.nome}>{phase.nome}</span>
            <div className="semafix-phase-timeline" aria-label={`${green}s verde, ${phaseYellow}s giallo, ${red}s rosso`}>
              <i className="green" style={{ width: `${(green / cycle) * 100}%` }} />
              <i className="yellow" style={{ width: `${(phaseYellow / cycle) * 100}%` }} />
              <i className="red" style={{ width: `${(red / cycle) * 100}%` }} />
            </div>
            <b>{green}/{phaseYellow}/{red}s</b>
          </div>
        );
      })}
    </div>
  );
}

export default function IntersectionEditor({
  intersection,
  signal,
  signalPlan,
  measurements = [],
  measuring = false,
  crossings = [],
  onMeasuringChange,
  onMeasure,
  onClose,
}) {
  const [messageApi, contextHolder] = message.useMessage();
  const [maneuvers, setManeuvers] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [conflicts, setConflicts] = useState([]);
  const [cycle, setCycle] = useState(() => phaseSeed(signalPlan).cycle);
  const [yellow, setYellow] = useState(() => phaseSeed(signalPlan).yellow);
  const [allRed, setAllRed] = useState(() => phaseSeed(signalPlan).allRed);
  const [optimizing, setOptimizing] = useState(false);
  const [optimization, setOptimization] = useState(null);
  const [hoveredManeuverId, setHoveredManeuverId] = useState(null);
  const [trafficLights, setTrafficLights] = useState(() => (
    loadIntersectionTrafficLights(intersection.id)
  ));
  /** Active drawing tool: `maneuver` | `traffic` | null. */
  const [tool, setTool] = useState(null);
  /** Active map mode: `edit` | `delete` | null. */
  const [mode, setMode] = useState(null);
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  /** Bumped to remount layers and discard unsaved vertex drags. */
  const [layerVersion, setLayerVersion] = useState(0);
  const [map, setMap] = useState(null);
  /** §10: resizable info panel — width dragged via the left-edge handle. */
  const [panelWidth, setPanelWidth] = useState(300);
  /** §6: the Conflicts tab needs more room, so the panel auto-expands there. */
  const [activeTab, setActiveTab] = useState('maneuvers');
  const editorMapRef = useRef(null);
  const maneuverLayersRef = useRef(new Map());
  const trafficLayersRef = useRef(new Map());
  const trafficLightsRef = useRef(trafficLights);
  /* Read on purpose through a ref: editing the popup plan afterwards must not
     overwrite timings already adjusted here. */
  const signalPlanRef = useRef(signalPlan);
  const center = useMemo(() => [Number(intersection.lat), Number(intersection.lng)], [intersection]);

  useEffect(() => { trafficLightsRef.current = trafficLights; }, [trafficLights]);
  useEffect(() => { signalPlanRef.current = signalPlan; }, [signalPlan]);

  const applyPhaseSeed = useCallback((plan) => {
    const seed = phaseSeed(plan);
    setCycle(seed.cycle);
    setYellow(seed.yellow);
    setAllRed(seed.allRed);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setManeuvers([]);
    setConflicts([]);
    setOptimization(null);
    setTool(null);
    setMode(null);
    setSelection(EMPTY_SELECTION);
    applyPhaseSeed(signalPlanRef.current);
    setTrafficLights(loadIntersectionTrafficLights(intersection.id));
    Promise.all([
      loadIntersectionManeuvers(intersection.id),
      loadConflicts(intersection.id),
    ])
      .then(([items, loadedConflicts]) => {
        if (active) {
          setManeuvers(items.map(normalizeManeuver));
          setConflicts(Array.isArray(loadedConflicts) ? loadedConflicts : []);
          const savedPlan = loadOptimizedPlan(intersection.id);
          if (savedPlan) setOptimization(savedPlan);
        }
      })
      .catch((error) => {
        if (active) messageApi.error(error.message || 'Impossibile caricare le manovre.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [applyPhaseSeed, intersection.id, messageApi]);

  const persistTrafficLights = useCallback((next) => {
    trafficLightsRef.current = next;
    setTrafficLights(next);
    saveIntersectionTrafficLights(intersection.id, next);
  }, [intersection.id]);

  /* ── Drawing tools ────────────────────────────────────────────────
     Leaflet's own draw handlers are driven directly (as on the main
     SEMAFIX map) so the toolbar can follow the project design system
     instead of the leaflet-draw chrome. */
  useEffect(() => {
    if (!map || !tool) return undefined;

    const handler = tool === 'maneuver'
      ? new L.Draw.Polyline(map, {
        shapeOptions: MANEUVER_SHAPE,
        showLength: true,
        metric: true,
        allowIntersection: true,
        guidelineDistance: 8,
      })
      : new L.Draw.Marker(map, { icon: trafficLightEditorIcon() });

    const onCreated = (event) => {
      if (tool === 'maneuver') {
        setDraft({ latlngs: event.layer.getLatLngs().map(({ lat, lng }) => ({ lat, lng })) });
      } else {
        const { lat, lng } = event.layer.getLatLng();
        persistTrafficLights([...trafficLightsRef.current, { id: newLocalId('traffic'), lat, lng }]);
        messageApi.success('Semaforo posizionato');
      }
      setTool(null);
    };
    const onStop = () => setTool(null);

    map.on(L.Draw.Event.CREATED, onCreated);
    map.on(L.Draw.Event.DRAWSTOP, onStop);
    handler.enable();

    return () => {
      map.off(L.Draw.Event.CREATED, onCreated);
      map.off(L.Draw.Event.DRAWSTOP, onStop);
      handler.disable();
    };
  }, [map, messageApi, persistTrafficLights, tool]);

  /* The ruler is shared with the SEMAFIX overview map: the same toggle arms
     both views so a distance can be taken from either one. */
  const { start: measurementStart, cursor: measurementCursor } = useDistanceMeasurement({
    map,
    active: measuring,
    onComplete: onMeasure,
  });

  /* Vertex handles on maneuvers, only while the edit mode is on. */
  useEffect(() => {
    const editable = mode === 'edit';
    maneuverLayersRef.current.forEach((layer) => {
      if (!layer?.editing) return;
      if (editable) layer.editing.enable();
      else layer.editing.disable();
    });
  }, [layerVersion, maneuvers, mode]);

  useEffect(() => () => {
    maneuverLayersRef.current.forEach((layer) => {
      try { layer?.editing?.disable(); } catch (_) { /* layer already detached */ }
    });
  }, []);

  const activateTool = (next) => {
    setMode(null);
    setSelection(EMPTY_SELECTION);
    onMeasuringChange?.(false);
    setTool((current) => (current === next ? null : next));
  };

  const toggleMeasuring = () => {
    setTool(null);
    setMode(null);
    setSelection(EMPTY_SELECTION);
    onMeasuringChange?.(!measuring);
  };

  const activateMode = (next) => {
    setTool(null);
    setSelection(EMPTY_SELECTION);
    onMeasuringChange?.(false);
    setMode((current) => {
      if (current === next) {
        if (current === 'edit') setLayerVersion((version) => version + 1);
        return null;
      }
      return next;
    });
  };

  const cancelMode = () => {
    if (mode === 'edit') setLayerVersion((version) => version + 1);
    setSelection(EMPTY_SELECTION);
    setMode(null);
  };

  const saveGeometry = async () => {
    const updates = [];
    const nextManeuvers = maneuvers.map((maneuver) => {
      const layer = maneuverLayersRef.current.get(maneuver.id);
      if (!layer) return maneuver;
      const latlngs = layer.getLatLngs().map(({ lat, lng }) => ({ lat, lng }));
      if (sameGeometry(maneuver.latlngs, latlngs)) return maneuver;
      const updated = { ...maneuver, latlngs };
      updates.push(updateManeuver(maneuver.id, maneuverPayload(updated, latlngs)));
      return updated;
    });

    let movedLights = false;
    const nextTrafficLights = trafficLights.map((trafficLight) => {
      const marker = trafficLayersRef.current.get(trafficLight.id);
      if (!marker) return trafficLight;
      const { lat, lng } = marker.getLatLng();
      if (lat === trafficLight.lat && lng === trafficLight.lng) return trafficLight;
      movedLights = true;
      return { ...trafficLight, lat, lng };
    });

    setMode(null);
    if (!updates.length && !movedLights) {
      messageApi.info('Nessuna modifica da salvare');
      return;
    }
    setManeuvers(nextManeuvers);
    if (movedLights) persistTrafficLights(nextTrafficLights);
    if (!updates.length) {
      messageApi.success('Posizione dei semafori aggiornata');
      return;
    }
    try {
      await Promise.all(updates);
      messageApi.success('Geometria aggiornata');
    } catch (error) {
      messageApi.error(error.message || 'Aggiornamento della geometria non riuscito.');
    }
  };

  const removeManeuvers = async (ids) => {
    const targets = maneuvers.filter((item) => ids.includes(item.id));
    if (!targets.length) return;
    setManeuvers((current) => current.filter((item) => !ids.includes(item.id)));
    const nextConflicts = conflicts.filter(([left, right]) => !targets.some(
      (item) => left.startsWith(`${item.title} -`) || right.startsWith(`${item.title} -`),
    ));
    if (nextConflicts.length !== conflicts.length) {
      setConflicts(nextConflicts);
      saveConflicts(intersection.id, nextConflicts).catch(() => {
        messageApi.warning('La matrice dei conflitti non è stata aggiornata.');
      });
    }
    try {
      await Promise.all(ids.map(deleteManeuver));
      messageApi.success(ids.length > 1 ? `${ids.length} manovre eliminate` : 'Manovra eliminata');
    } catch (error) {
      messageApi.error(error.message || 'Eliminazione non riuscita.');
    }
  };

  const removeTrafficLights = (ids) => {
    if (!ids.length) return;
    persistTrafficLights(trafficLightsRef.current.filter((item) => !ids.includes(item.id)));
    messageApi.success(ids.length > 1 ? `${ids.length} semafori eliminati` : 'Semaforo eliminato');
  };

  const confirmSelectionRemoval = async () => {
    const { maneuvers: maneuverIds, trafficLights: trafficLightIds } = selection;
    setSelection(EMPTY_SELECTION);
    setMode(null);
    removeTrafficLights(trafficLightIds);
    await removeManeuvers(maneuverIds);
  };

  const onManeuverClick = (maneuver) => {
    if (mode === 'delete') {
      setSelection((current) => ({ ...current, maneuvers: toggleId(current.maneuvers, maneuver.id) }));
      return;
    }
    if (!mode) setEditing(maneuver);
  };

  const onTrafficLightClick = (trafficLight) => {
    if (mode !== 'delete') return;
    setSelection((current) => ({
      ...current,
      trafficLights: toggleId(current.trafficLights, trafficLight.id),
    }));
  };

  const closeModal = () => {
    setDraft(null);
    setEditing(null);
  };

  const saveManeuver = async (data) => {
    const latlngs = draft?.latlngs || editing?.latlngs;
    const payload = { ...data, latlngs };
    if (editing?.id) {
      const saved = normalizeManeuver(await updateManeuver(editing.id, payload));
      setManeuvers((current) => current.map((item) => (item.id === editing.id ? saved : item)));
    } else {
      const saved = normalizeManeuver(await createManeuver(intersection.id, payload));
      setManeuvers((current) => [...current, saved]);
    }
    closeModal();
    messageApi.success(editing ? 'Manovra aggiornata' : 'Manovra aggiunta');
  };

  const runOptimization = async () => {
    setOptimizing(true);
    try {
      const result = await optimizePhases({
        Tc: Number(cycle),
        tempogiallo: Number(yellow),
        tempotuttorosso: Number(allRed),
        fasi: maneuvers.map((maneuver) => {
          const base = { nome: maneuver.title, tipo: maneuver.inputType };
          if (maneuver.inputType === 'pedonale') {
            return { ...base, verde_min_ped: Number(maneuver.verdeMin || 0) };
          }
          return {
            ...base,
            flusso: maneuver.flussi.reduce((sum, flow) => sum + Number(flow.value || 0), 0),
            cap_sat: Number(maneuver.capSatResult || 1200),
          };
        }),
        conflitti: conflicts,
      }, intersection.id);
      setOptimization(result);
      messageApi.success('Piano ottimizzato e salvato come riferimento corrente');
    } catch (error) {
      messageApi.error(error.message || 'Ottimizzazione non riuscita.');
    } finally {
      setOptimizing(false);
    }
  };

  const handleConflictsChange = (nextConflicts) => {
    setConflicts(nextConflicts);
    saveConflicts(intersection.id, nextConflicts).catch(() => {
      messageApi.warning('I conflitti non sono stati salvati.');
    });
  };

  /** §10: drag the panel's left edge to trade space between map and info. */
  const startPanelResize = (event) => {
    event.preventDefault();
    const minWidth = 240;
    const onMove = (moveEvent) => {
      const clientX = moveEvent.touches ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const maxWidth = Math.max(minWidth, window.innerWidth * 0.6);
      const next = Math.min(maxWidth, Math.max(minWidth, window.innerWidth - clientX - 16));
      setPanelWidth(next);
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
      document.body.style.cursor = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove);
    window.addEventListener('touchend', onUp);
    document.body.style.cursor = 'col-resize';
  };

  const selectionCount = selection.maneuvers.length + selection.trafficLights.length;
  const visibleManeuvers = maneuvers.filter((item) => item.visible);
  const draftGeometry = draft?.latlngs || editing?.latlngs || [];

  return (
    <section
      className="semafix-intersection-editor"
      aria-label="Editor intersezione"
      style={{ '--semafix-editor-panel-w': `${activeTab === 'conflicts' ? Math.max(panelWidth, 560) : Math.max(panelWidth, 460)}px` }}
    >
      {contextHolder}
      <div className={`semafix-editor-map${tool || measuring ? ' is-drawing' : ''}${mode === 'delete' ? ' is-removing' : ''}`}>
        <MapContainer center={center} zoom={19} zoomControl={false} attributionControl={false}>
          <TraffixTileLayer theme="satellite" />
          <MapRefCapture mapRef={editorMapRef} onMapReady={setMap} />
          <Marker position={center} icon={intersectionIcon(intersection, { selected: true })}>
            <Tooltip direction="top">{signal?.name || intersection.name}</Tooltip>
          </Marker>

          {visibleManeuvers.map((maneuver) => {
            const marked = selection.maneuvers.includes(maneuver.id);
            return (
              <Polyline
                key={`${maneuver.id}-${layerVersion}`}
                ref={(layer) => {
                  if (layer) maneuverLayersRef.current.set(maneuver.id, layer);
                  else maneuverLayersRef.current.delete(maneuver.id);
                }}
                positions={maneuver.latlngs}
                pathOptions={{
                  color: maneuver.color,
                  weight: marked || hoveredManeuverId === maneuver.id ? 8 : 6,
                  opacity: hoveredManeuverId && hoveredManeuverId !== maneuver.id
                    ? 0.2
                    : marked ? 0.5 : 0.95,
                  dashArray: marked ? '8 8' : undefined,
                }}
                eventHandlers={{
                  click: () => onManeuverClick(maneuver),
                  mouseover: () => setHoveredManeuverId(maneuver.id),
                  mouseout: () => setHoveredManeuverId(null),
                }}
              >
                {/* Anchored at the origin: every manoeuvre crosses the centre,
                    so centred labels would stack on top of each other. */}
                <Tooltip permanent direction="top" position={maneuver.latlngs[0]}>{maneuver.title}</Tooltip>
              </Polyline>
            );
          })}

          {visibleManeuvers.map((maneuver) => {
            const arrow = endpointIcon(maneuver.latlngs, maneuver.color);
            const last = maneuver.latlngs[maneuver.latlngs.length - 1];
            return arrow && last
              ? <Marker key={`arrow-${maneuver.id}`} position={last} icon={arrow} interactive={false} />
              : null;
          })}

          {trafficLights.map((trafficLight, index) => (
            <Marker
              key={`${trafficLight.id}-${layerVersion}`}
              ref={(layer) => {
                if (layer) trafficLayersRef.current.set(trafficLight.id, layer);
                else trafficLayersRef.current.delete(trafficLight.id);
              }}
              position={[trafficLight.lat, trafficLight.lng]}
              icon={trafficLightEditorIcon()}
              draggable={mode === 'edit'}
              opacity={selection.trafficLights.includes(trafficLight.id) ? 0.45 : 1}
              eventHandlers={{ click: () => onTrafficLightClick(trafficLight) }}
            >
              <Tooltip direction="top">Semaforo {index + 1}</Tooltip>
            </Marker>
          ))}

          <MeasurementLayer
            measurements={measurements}
            start={measurementStart}
            cursor={measurementCursor}
          />

          <div className="semafix-editor-map-controls">
            <CurrentLocationButton />
            <ZoomButtons />
          </div>
        </MapContainer>

        <div className="semafix-editor-toolbar" role="toolbar" aria-label="Strumenti di disegno">
          <AntTooltip title="Disegna manovra" placement="left">
            <button
              className={`sidebar-button ${tool === 'maneuver' ? 'active' : ''}`}
              onClick={() => activateTool('maneuver')}
              aria-label="Disegna manovra"
              aria-pressed={tool === 'maneuver'}
            >
              <TbArrowCurveRight />
            </button>
          </AntTooltip>
          <AntTooltip title="Aggiungi semaforo" placement="left">
            <button
              className={`sidebar-button ${tool === 'traffic' ? 'active' : ''}`}
              onClick={() => activateTool('traffic')}
              aria-label="Aggiungi semaforo"
              aria-pressed={tool === 'traffic'}
            >
              <img src={TlGreenWaveIcon} alt="" />
            </button>
          </AntTooltip>
          <AntTooltip title="Misura distanza (su entrambe le mappe)" placement="left">
            <button
              className={`sidebar-button ${measuring ? 'active' : ''}`}
              onClick={toggleMeasuring}
              aria-label="Misura distanza su entrambe le mappe"
              aria-pressed={measuring}
            >
              <TbRulerMeasure />
            </button>
          </AntTooltip>
          <AntTooltip title="Modifica geometria" placement="left">
            <button
              className={`sidebar-button ${mode === 'edit' ? 'active' : ''}`}
              onClick={() => activateMode('edit')}
              aria-label="Modifica geometria"
              aria-pressed={mode === 'edit'}
              disabled={!maneuvers.length && !trafficLights.length}
            >
              <EditOutlined />
            </button>
          </AntTooltip>
          <AntTooltip title="Elimina elementi" placement="left">
            <button
              className={`sidebar-button ${mode === 'delete' ? 'active' : ''}`}
              onClick={() => activateMode('delete')}
              aria-label="Elimina elementi"
              aria-pressed={mode === 'delete'}
              disabled={!maneuvers.length && !trafficLights.length}
            >
              <DeleteOutlined />
            </button>
          </AntTooltip>
        </div>
      </div>

      <div className="semafix-editor-overlay-stack">
        <header className="semafix-editor-header">
          <span className="semafix-editor-tag">EDITOR</span>
          <h2>{signal?.name || intersection.name || 'Intersezione semaforizzata'}</h2>
          <AntTooltip title="Chiudi editor">
            <button onClick={onClose} aria-label="Chiudi editor"><CloseOutlined /></button>
          </AntTooltip>
        </header>

        <div className={`semafix-editor-hint${tool || mode || measuring ? '' : ' is-idle'}`} role="status">
          {tool === 'maneuver' && (
            <p>
              <b>Disegna manovra:</b> clicca l’origine, poi la destinazione e termina
              con un doppio clic. La freccia indica il verso.
            </p>
          )}
          {tool === 'traffic' && <p><b>Aggiungi semaforo:</b> clicca sulla mappa per posizionare l’impianto.</p>}
          {measuring && (
            <p>
              <b>Misura distanza:</b>{' '}
              {measurementStart
                ? 'clicca il secondo punto per completare la misura.'
                : 'clicca il punto iniziale e poi il punto finale, su questa mappa o su quella SEMAFIX.'}
            </p>
          )}
          {mode === 'edit' && <p><b>Modifica:</b> trascina i vertici delle manovre e i semafori.</p>}
          {mode === 'delete' && (
            <p>
              <b>Elimina:</b> seleziona sulla mappa gli elementi da rimuovere
              {selectionCount ? ` · ${selectionCount} selezionati` : ''}
            </p>
          )}
          {!tool && !mode && !measuring && (
            <p>
              <b>Manovra</b> e <b>semaforo</b> per disegnare, <b>modifica</b> e <b>elimina</b>{' '}
              per correggere: gli strumenti sono in alto a destra.
            </p>
          )}
          {mode === 'edit' && <button type="button" className="primary" onClick={saveGeometry}>Salva</button>}
          {mode === 'delete' && (
            <button
              type="button"
              className="danger"
              disabled={!selectionCount}
              onClick={confirmSelectionRemoval}
            >
              Elimina{selectionCount ? ` (${selectionCount})` : ''}
            </button>
          )}
          {(tool || mode || measuring) && (
            <button
              type="button"
              onClick={() => {
                if (mode) cancelMode();
                else if (tool) setTool(null);
                else onMeasuringChange?.(false);
              }}
            >
              Annulla
            </button>
          )}
        </div>
      </div>

      <div className="semafix-editor-panel">
        <div
          className="semafix-editor-resize-handle"
          role="separator"
          aria-label="Ridimensiona pannello"
          onMouseDown={startPanelResize}
          onTouchStart={startPanelResize}
        />
        {loading ? (
          <div className="semafix-editor-loading"><Spin /><span>Caricamento manovre…</span></div>
        ) : (
          <Tabs
            size="small"
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'maneuvers',
                label: `Manovre (${maneuvers.length})`,
                children: (
                  <>
                    <div className="semafix-editor-section-head">
                      <h3>Manovre</h3>
                      <Button size="small" type="primary" onClick={() => activateTool('maneuver')}>Aggiungi manovra</Button>
                    </div>
                    {maneuvers.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description="Disegna la prima manovra sulla mappa"
                      />
                    ) : (
                      <div className="semafix-editor-table-wrap">
                        <table className="semafix-editor-table">
                          <thead>
                            <tr>
                              <th>Direzione</th>
                              <th>Tipo</th>
                              <th>Corsie</th>
                              <th>Larghezza</th>
                              <th>Flusso</th>
                              <th>Capacità</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {maneuvers.map((maneuver, index) => {
                              const flow = (maneuver.flussi || []).reduce((sum, item) => sum + Number(item.value || 0), 0);
                              return (
                                <tr
                                  key={maneuver.id}
                                  className={hoveredManeuverId === maneuver.id ? 'is-highlighted' : ''}
                                  onMouseEnter={() => setHoveredManeuverId(maneuver.id)}
                                  onMouseLeave={() => setHoveredManeuverId(null)}
                                >
                                  <td>{maneuverRouteLabel(maneuver) || maneuver.title}</td>
                                  <td>{maneuver.inputType === 'pedonale' ? 'Pedonale' : movementTypeLabel(maneuver.movementType)}</td>
                                  <td>{maneuver.lanes || 1}</td>
                                  <td>{maneuver.laneWidth || 3.25} m</td>
                                  <td>{flow || '—'}</td>
                                  <td>{maneuver.capSatResult ?? '—'}</td>
                                  <td className="actions">
                                    <button
                                      className="icon"
                                      aria-label={maneuver.visible ? 'Nascondi' : 'Mostra'}
                                      onClick={() => setManeuvers((current) => current.map((item) => (
                                        item.id === maneuver.id ? { ...item, visible: !item.visible } : item
                                      )))}
                                    >
                                      {maneuver.visible ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                    </button>
                                    <button className="icon" aria-label={`Modifica manovra ${index + 1}`} onClick={() => setEditing(maneuver)}><EditOutlined /></button>
                                    <Popconfirm title="Eliminare questa manovra?" okText="Sì" cancelText="No" onConfirm={() => removeManeuvers([maneuver.id])}>
                                      <button className="icon danger" aria-label="Elimina"><DeleteOutlined /></button>
                                    </Popconfirm>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="semafix-editor-section-head">
                      <h3>Attraversamenti pedonali</h3>
                      <span>{crossings.length}</span>
                    </div>
                    {crossings.length === 0 ? (
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessun attraversamento su questo impianto" />
                    ) : (
                      <div className="semafix-editor-table-wrap">
                        <table className="semafix-editor-table">
                          <thead>
                            <tr>
                              <th>ID</th>
                              <th>Posizione</th>
                              <th>Lunghezza</th>
                            </tr>
                          </thead>
                          <tbody>
                            {crossings.map((crossing, index) => (
                              <tr key={crossing.id}>
                                <td>P{index + 1}</td>
                                <td>{crossing.name || '—'}</td>
                                <td>{crossing.length} m</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {trafficLights.length > 0 && (
                      <>
                        <div className="semafix-editor-section-head">
                          <h3>Semafori</h3>
                          <span>{trafficLights.length}</span>
                        </div>
                        <div className="semafix-editor-list">
                          {trafficLights.map((trafficLight, index) => (
                            <div className="semafix-editor-list-item traffic" key={trafficLight.id}>
                              <span className="traffic-symbol">🚦</span>
                              <button
                                className="details"
                                onClick={() => editorMapRef.current?.flyTo([trafficLight.lat, trafficLight.lng], 20)}
                              >
                                <b>Semaforo {index + 1}</b>
                                <span>{trafficLight.lat.toFixed(5)}, {trafficLight.lng.toFixed(5)}</span>
                              </button>
                              <Popconfirm title="Eliminare questo semaforo?" okText="Sì" cancelText="No" onConfirm={() => removeTrafficLights([trafficLight.id])}>
                                <button className="icon danger" aria-label="Elimina semaforo"><DeleteOutlined /></button>
                              </Popconfirm>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ),
              },
              {
                key: 'conflicts',
                label: 'Conflitti',
                children: (
                  <ConflictMatrix
                    maneuvers={maneuvers}
                    conflicts={conflicts}
                    onChange={handleConflictsChange}
                  />
                ),
              },
              {
                key: 'phases',
                label: 'Piano semaforico',
                children: (
                  <>
                    {signalPlan && (
                      <div className="semafix-editor-plan-source">
                        <span>
                          Piano Semaforizzata: verde {Math.round(Number(signalPlan.green) || 0)}s ·
                          giallo {Math.round(Number(signalPlan.yellow) || 0)}s ·
                          rosso {Math.round(Number(signalPlan.red) || 0)}s ·
                          ciclo {phaseSeed(signalPlan).cycle}s
                        </span>
                        <button type="button" onClick={() => applyPhaseSeed(signalPlan)}>
                          Riprendi
                        </button>
                      </div>
                    )}
                    <div className="semafix-editor-timings">
                      <label><span>Durata ciclo (s)</span><InputNumber size="small" min={1} value={cycle} onChange={setCycle} /></label>
                      <label><span>Tempo di giallo (s)</span><InputNumber size="small" min={0} value={yellow} onChange={setYellow} /></label>
                      <label>
                        <AntTooltip title="Tempo di sgombero fra due fasi">
                          <span>Tempo di rosso generale (s)</span>
                        </AntTooltip>
                        <InputNumber size="small" min={0} value={allRed} onChange={setAllRed} />
                      </label>
                    </div>
                    <div className="semafix-editor-plan-summary">
                      <span><i className="green" /> Verde configurato per manovra</span>
                      <span><i className="yellow" /> Giallo {yellow}s</span>
                      <span><i className="red" /> Rosso generale {allRed}s</span>
                    </div>
                  </>
                ),
              },
              {
                key: 'optimization',
                label: 'Ottimizzazione',
                children: (
                  <>
                    <Button
                      className="semafix-editor-optimize"
                      type="primary"
                      size="small"
                      icon={<ThunderboltOutlined />}
                      loading={optimizing}
                      disabled={!maneuvers.length}
                      onClick={runOptimization}
                    >
                      Ottimizza piano semaforico
                    </Button>

                    <OptimizedTimeline
                      optimization={optimization}
                      maneuvers={maneuvers}
                      cycle={cycle}
                      hoveredId={hoveredManeuverId}
                      onHover={setHoveredManeuverId}
                    />
                  </>
                ),
              },
            ]}
          />
        )}
      </div>

      <ManeuverModal
        open={Boolean(draft || editing)}
        maneuver={editing}
        defaultTitle={`Manovra ${maneuvers.length + 1}`}
        geometry={{
          points: draftGeometry.length,
          lengthMeters: Math.round(maneuverLengthMeters(draftGeometry)),
        }}
        onCancel={closeModal}
        onSave={saveManeuver}
      />
    </section>
  );
}
